/**
 * useProactiveOffers — live "Argo can help" offers for the dock.
 *
 * Subscribes to the people collection (the same seam every agent surface
 * reads), runs the PURE `composeOffers` engine with the real research-worker
 * gate (enrich.isConfigured()), and drops any offer currently inside its
 * 7-day dismissal window. The hook owns ONLY the wiring:
 *
 *   • accept(offer) fires the REAL mechanism —
 *       research → enrichProspect(person) + updatePerson (the People view's
 *                  own enrichment run, reused verbatim),
 *       reminder → queueApproval(schedule_follow_up) (the governed lane).
 *   • dismiss(offer) hides it for 7 days (offersDismissal, persisted).
 *
 * Every judgement lives in the tested engine; nothing here manufactures an
 * offer. Accepting returns a confirmation line the caller drops into the
 * transcript, so the dock never has to know what a research run "means".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { subscribePeople, updatePerson, logPersonActivity } from '../db/people';
import type { Person } from '../db/types';
import { composeOffers, type Offer } from './offers';
import {
  dismissOffer,
  filterDismissed,
  readDismissals,
  subscribeOfferDismissals,
} from './offersDismissal';
import { enrichInfo, enrichProspect } from './enrich';
import { queueApproval } from './approvals';

export interface UseProactiveOffers {
  /** Up to two grounded offers, dismissed ones removed. */
  offers: Offer[];
  /** Fire the offer's real mechanism; returns a transcript confirmation line. */
  accept: (offer: Offer) => Promise<string>;
  /** Hide the offer for 7 days (persisted). */
  dismiss: (offer: Offer) => void;
}

export function useProactiveOffers(): UseProactiveOffers {
  const [people, setPeople] = useState<Person[]>([]);
  // Bump to force re-filter when a dismissal lands (localStorage → listener).
  const [dismissTick, setDismissTick] = useState(0);

  useEffect(() => {
    let active = true;
    const unsub = subscribePeople((r) => active && setPeople(r));
    const unsubDismiss = subscribeOfferDismissals(() =>
      active ? setDismissTick((n) => n + 1) : undefined,
    );
    return () => {
      active = false;
      unsub();
      unsubDismiss();
    };
  }, []);

  const offers = useMemo(() => {
    const now = Date.now();
    const all = composeOffers({ people, researchConfigured: enrichInfo().configured, now });
    return filterDismissed(all, readDismissals(now), now);
    // dismissTick forces recompute after a dismiss without re-subscribing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, dismissTick]);

  const accept = useCallback(
    async (offer: Offer): Promise<string> => {
      if (!offer.action) {
        // Gated offer — no mechanism to fire (worker unconfigured).
        return 'Research runs once the Claude worker is connected (Admin → Integrations).';
      }
      if (offer.action.type === 'research') {
        // The EXACT People-view enrichment run: mark pending, run, persist.
        const { personId, personName } = offer.action;
        const person = people.find((p) => p.id === personId);
        if (!person) return `Couldn't find ${personName} to research.`;
        await updatePerson(personId, {
          enrichment: { status: 'pending', evaluatedAt: new Date().toISOString() },
        });
        const result = await enrichProspect({
          name: person.name,
          company: person.company,
          title: person.title,
          linkedinUrl: person.linkedinUrl,
          website: person.website,
        });
        await updatePerson(personId, {
          enrichment: result,
          ...(result.fitScore !== undefined ? { score: result.fitScore } : {}),
        });
        const actor = enrichInfo().configured ? 'Enrichment worker' : 'Argo (simulated)';
        await logPersonActivity(
          personId,
          'system',
          `Research ${result.status === 'done' ? 'completed' : result.status} — ${
            result.summary ?? 'no summary'
          }`,
          actor,
        );
        return `Researching ${person.company || person.name} — read saved to their record.`;
      }
      // Reminder — queue the governed schedule_follow_up proposal.
      await queueApproval(offer.action.action, 'autopilot', offer.evidence);
      return `Queued a follow-up reminder for ${offer.action.action.personName} — approve it in the Dashboard inbox.`;
    },
    [people],
  );

  const dismiss = useCallback((offer: Offer) => {
    dismissOffer(offer.id);
  }, []);

  return { offers, accept, dismiss };
}

/**
 * Tiny summary hook for the session greeting — "2 offers waiting" (or null
 * when there are none). Same engine + gate + dismissal filter as the dock, so
 * the greeting and the dock can never disagree about what's on offer.
 */
export function useOffersSummary(): { count: number; label: string | null } {
  const { offers } = useProactiveOffers();
  const count = offers.length;
  return {
    count,
    label:
      count === 0 ? null : count === 1 ? '1 offer waiting' : `${count} offers waiting`,
  };
}
